import { useEffect, useState } from "react";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { UploadSimple, CheckCircle, Clock, ArrowsClockwise, DownloadSimple, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtPeriod } from "@/lib/format";

const STATUS_META = {
    submitted: { icon: CheckCircle, color: "text-fp-navy", label: "Submitted", bg: "bg-fp-navy/10" },
    approved: { icon: CheckCircle, color: "text-fp-success", label: "Approved", bg: "bg-fp-success/10" },
    review: { icon: Clock, color: "text-fp-ochre", label: "In Review", bg: "bg-fp-ochre/20" },
    extension_granted: { icon: Clock, color: "text-fp-secondary", label: "Extension", bg: "bg-fp-panel" },
    pending: { icon: Clock, color: "text-fp-terracotta", label: "Pending", bg: "bg-fp-terracotta/10" },
    overdue: { icon: Clock, color: "text-fp-terracotta", label: "Overdue", bg: "bg-fp-terracotta/15" },
    waived: { icon: CheckCircle, color: "text-fp-muted", label: "Waived", bg: "bg-fp-border" },
};

export default function Uploads() {
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const load = () => client.get("/uploads/status").then((r) => setData(r.data));
    useEffect(() => {
        load();
        const id = setInterval(load, 8000); // Live progress every 8s
        return () => clearInterval(id);
    }, []);

    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;

    const submitted = data.entities.filter((e) => e.status !== "pending" && e.status !== "overdue").length;
    const total = data.entities.length;
    const pct = Math.round((submitted / total) * 100);

    const bulkDownload = async () => {
        setDownloading(true);
        const t = toast.loading("Generating submission pack — 336 Excel files…");
        try {
            const token = localStorage.getItem("fp_token");
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/entities/bulk-download-xlsx`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `Submission_Pack.zip`; a.click();
            toast.success(`Downloaded ${(blob.size / 1024 / 1024).toFixed(1)} MB · 336 files`, { id: t });
        } catch (e) {
            toast.error("Download failed", { id: t });
        } finally { setDownloading(false); }
    };

    return (
        <div>
            <PageHeader
                overline={`Data Ingestion · Cycle ${fmtPeriod(data.cycle)}`}
                title="Monthly Submissions"
                description="Each operating entity submits a standardised Excel template. Consolidation runs automatically once all submissions are received and approved."
                period={data.cycle}
            >
                <button data-testid="bulk-download" onClick={bulkDownload} disabled={downloading} className="inline-flex items-center gap-2 bg-fp-ink text-white px-5 py-2.5 font-sans text-xs hover:bg-fp-ink-hover transition-colors disabled:opacity-50">
                    <Package size={14} weight="fill" /> {downloading ? "Generating…" : "Download All 168 Entities · .zip"}
                </button>
            </PageHeader>

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                {/* Progress bar */}
                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Consolidation Progress</div>
                            <h3 className="font-serif text-2xl text-fp-ink">{submitted} of {total} entities</h3>
                        </div>
                        <div className="font-mono text-4xl text-fp-ink">{pct}%</div>
                    </div>
                    <div className="w-full h-1.5 bg-fp-panel">
                        <div className="h-full bg-fp-ink transition-all duration-700" style={{ width: `${pct}%` }} data-testid="progress-bar" />
                    </div>
                </div>

                {/* Upload zone */}
                <div className="bg-white border-2 border-dashed border-fp-border p-16 text-center hover:border-fp-ink/40 transition-colors" data-testid="upload-zone">
                    <UploadSimple size={40} weight="thin" className="text-fp-secondary mx-auto mb-4" />
                    <div className="font-serif text-2xl text-fp-ink mb-2">Upload Entity Submission</div>
                    <p className="font-sans text-sm text-fp-secondary max-w-lg mx-auto mb-6">Drop the entity's monthly Excel template here (.xlsx). Automated validation, mapping and consolidation runs in the background.</p>
                    <button
                        data-testid="mock-upload-btn"
                        onClick={() => toast.success("Upload validated (demo). Entity submission accepted.", { description: "Consolidation will re-run within 60 seconds." })}
                        className="inline-flex items-center gap-2 bg-fp-ink text-white px-6 py-2.5 font-sans text-sm hover:bg-fp-ink-hover transition-colors"
                    >
                        <UploadSimple size={14} /> Select Excel File
                    </button>
                    <div className="font-mono text-[10px] text-fp-muted uppercase tracking-widest mt-6">Demo mode · UI stub for month-end ingestion pipeline</div>
                </div>

                {/* Submissions grid */}
                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Entity Submissions</div>
                        <h3 className="font-serif text-2xl text-fp-ink">Cycle Tracker</h3>
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-fp-ink">
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Entity</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Sector</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Country</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Submitter</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Submitted</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.entities.map((e) => {
                                const st = STATUS_META[e.status];
                                const Icon = st.icon;
                                const d = new Date(e.submitted_at);
                                return (
                                    <tr key={e.entity_id} className="border-b border-fp-border hover:bg-fp-bg transition-colors" data-testid={`upload-row-${e.entity_id}`}>
                                        <td className="py-3 font-sans text-sm text-fp-ink">{e.entity_name}</td>
                                        <td className="py-3 font-sans text-xs text-fp-secondary capitalize">{e.sector_id}</td>
                                        <td className="py-3 font-mono text-xs text-fp-secondary">{e.country_code}</td>
                                        <td className="py-3 font-sans text-sm text-fp-secondary">{e.submitter}</td>
                                        <td className="py-3 font-mono text-xs text-fp-secondary">{d.toLocaleDateString()}</td>
                                        <td className="py-3 text-right">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${st.bg} ${st.color} font-sans text-xs`}>
                                                <Icon size={12} weight="fill" /> {st.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
